import { Rectangle, Texture, type TextureSource } from 'pixi.js';
import { corsUrl, loadBitmap, type AssetCatalog } from './assets';
import { blastOutId } from './ball-blastout';
import { WEAPON_MOTION } from './ball-weapon-motion';

/**
 * Quadros por segundo da explosão. Os SWF de bomba trazem 24 (um deles 25), mas o Flash toca o clipe carregado na taxa do palco do jogo
 * (`DDT_Loading.swf`: 25) e a física avança um passo de 40 ms por quadro: 25 quadros por segundo.
 */
export const BLAST_FPS = 25;

interface RawFrame {
  frame: { x: number; y: number; w: number; h: number };
  spriteSourceSize?: { x: number; y: number; w: number; h: number };
  sourceSize?: { w: number; h: number };
}
interface RawAtlas {
  frames: Record<string, RawFrame>;
  animations?: Record<string, string[]>;
}

/** Explosão pronta: os quadros em ordem e o ponto da animação que cai no impacto (fração do tamanho do quadro). */
export interface BlastArt {
  frames: Texture[];
  anchor: { x: number; y: number };
}

/**
 * A arte de uma bomba: o projétil (um quadro, apontando para a direita), a explosão e a arma na mão do atirador (`shootMovie`: os quadros do clipe, do 1 em
 * diante, na célula de 114×95 do corpo). `null` onde o manifest não tem a arte.
 */
export interface BombArt {
  bullet: Texture | null;
  blast: BlastArt | null;
  weapon: Texture[] | null;
}

const BULLET_DIR = 'atlas/swf/Resource/image/bomb/blastout';
const BLAST_DIR = 'atlas/swf/Resource/image/bomb/bullet';

/**
 * Arte das bombas, já convertida dos SWF do legado (atlas `bomb/blastout/blastOut<id>` com o projétil `bullet<id>` e `bomb/bullet/bullet<id>` com
 * a explosão `blastOutMovie<id>`; os nomes das pastas do legado se cruzam). O legado nomeia as pastas ora `blastOut`, ora `blastout`: a busca ignora
 * maiúsculas. Cada bomba baixa uma vez; bomba sem arte devolve `null` e o desenho cai no projétil e na explosão simples.
 */
export class BombArtLibrary {
  private readonly cache = new Map<number, Promise<BombArt>>();
  private lower: Map<string, string> | null = null;

  constructor(private readonly assets: AssetCatalog) {}

  load(ballId: number): Promise<BombArt> {
    let cached = this.cache.get(ballId);
    if (!cached) {
      cached = this.build(ballId).catch(() => ({ bullet: null, blast: null, weapon: null }));
      this.cache.set(ballId, cached);
    }
    return cached;
  }

  /** Arte já carregada, ou `null` enquanto baixa (e dispara o download). */
  peek(ballId: number): BombArt | null {
    const ready = this.ready.get(ballId);
    if (ready) return ready;
    if (!this.requested.has(ballId)) {
      this.requested.add(ballId);
      void this.load(ballId).then((art) => this.ready.set(ballId, art));
    }
    return null;
  }

  private readonly ready = new Map<number, BombArt>();
  private readonly requested = new Set<number>();

  private resolve(path: string): string | null {
    this.lower ??= new Map(Object.keys(this.assets.manifest.assets).map((key) => [key.toLowerCase(), key]));
    return this.lower.get(path.toLowerCase()) ?? null;
  }

  private async atlas(dir: string, name: string): Promise<{ raw: RawAtlas; sheet: ImageBitmap } | null> {
    const base = `${dir}/${name}/Resource_image_bomb_${dir.endsWith('bullet') ? 'bullet' : 'blastout'}_${name}`;
    const json = this.resolve(`${base}.json`);
    const webp = this.resolve(`${base}.webp`);
    if (!json || !webp) return null;
    const response = await fetch(corsUrl(this.assets.url(json)));
    if (!response.ok) return null;
    const sheet = await loadBitmap(this.assets.url(webp));
    return sheet ? { raw: (await response.json()) as RawAtlas, sheet } : null;
  }

  private texture(source: TextureSource, f: RawFrame): Texture {
    const full = f.sourceSize ?? { w: f.frame.w, h: f.frame.h };
    const offset = f.spriteSourceSize ?? { x: 0, y: 0, w: f.frame.w, h: f.frame.h };
    return new Texture({
      source,
      frame: new Rectangle(f.frame.x, f.frame.y, f.frame.w, f.frame.h),
      orig: new Rectangle(0, 0, full.w, full.h),
      trim: new Rectangle(offset.x, offset.y, f.frame.w, f.frame.h),
    });
  }

  private async build(ballId: number): Promise<BombArt> {
    const [bulletAtlas, blastAtlas] = await Promise.all([this.atlas(BULLET_DIR, `blastout${ballId}`), this.atlas(BLAST_DIR, `bullet${blastOutId(ballId)}`)]);

    let bullet: Texture | null = null;
    const bulletFrame = bulletAtlas?.raw.frames[`bullet${ballId}/1`];
    if (bulletAtlas && bulletFrame) bullet = this.texture(Texture.from(bulletAtlas.sheet).source, bulletFrame);

    let blast: BlastArt | null = null;
    const id = blastOutId(ballId);
    const names = blastAtlas?.raw.animations?.[`blastOutMovie${id}`];
    if (blastAtlas && names && names.length > 0) {
      const frames = names.map((n) => blastAtlas.raw.frames[n]).filter((f): f is RawFrame => f !== undefined);
      // O legado não guarda o ponto de registro: o impacto fica no centro da área que todos os quadros cobrem juntos.
      let [minX, minY, maxX, maxY] = [Infinity, Infinity, -Infinity, -Infinity];
      for (const f of frames) {
        const o = f.spriteSourceSize ?? { x: 0, y: 0, w: f.frame.w, h: f.frame.h };
        if (f.frame.w <= 1 && f.frame.h <= 1) continue; // quadro vazio (1×1)
        minX = Math.min(minX, o.x);
        minY = Math.min(minY, o.y);
        maxX = Math.max(maxX, o.x + o.w);
        maxY = Math.max(maxY, o.y + o.h);
      }
      const size = frames[0]?.sourceSize ?? { w: 1, h: 1 };
      const sheetSource = Texture.from(blastAtlas.sheet).source;
      if (Number.isFinite(minX)) {
        blast = {
          frames: frames.map((f) => this.texture(sheetSource, f)),
          anchor: { x: (minX + maxX) / 2 / size.w, y: (minY + maxY) / 2 / size.h },
        };
      }
    }
    let weapon: Texture[] | null = null;
    const clip = WEAPON_MOTION[ballId]?.[0];
    const clipFrames = clip ? bulletAtlas?.raw.animations?.[clip]?.map((n) => bulletAtlas.raw.frames[n]) : undefined;
    if (bulletAtlas && clipFrames && clipFrames.every((f) => f !== undefined)) {
      const source = Texture.from(bulletAtlas.sheet).source;
      weapon = clipFrames.map((f) => this.texture(source, f!));
    }
    return { bullet, blast, weapon };
  }
}
